@file:OptIn(ExperimentalResourceApi::class)

package ui.location.detail


import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.foundation.shape.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.layout.*
import androidx.compose.ui.text.font.*
import androidx.compose.ui.unit.*
import compose.assets.*
import compose.assets.svg.*
import org.jetbrains.compose.resources.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.common.msg.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.*
import tools.*
import ui.widget.*

data class LocationArgs(
    var companyId: Long = -1,
    var locationId: Long = -1
)

@ComposableNavNode(LocationArgs::class)
@Composable
fun LocationUI() {
    with(LocationScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun LocationScope.Content(nnvl: LocationScope.Wrapper) {
    val deleteState = DialogState.remember()
    val saveState = nnvl.saveState()
    val actionState = DialogState.remember()

    DeleteDialog(deleteState, nnvl.data.count_device)
    GroupActionDialog(actionState)

    val toolbarActions = remember {
        buildList {
            if (groupActions.isNotEmpty() && nnvl.data.count_device > 0) {
                add(
                    ToolbarAction(
                        icon = Svg.Folder_wrench,
                        visible = vm.showGroupActions
                    ) {
                        actionState.show()
                    })
            }
            if (nnvl.canDelete) {
                add(ToolbarAction.delete { deleteState.show() })
            }
            if (nnvl.canEdit) {
                add(ToolbarAction.save(saveState) { action(LocationVM.Action.Save) })
            }
        }
    }

    Column(Modifier.fillMaxSize()) {
        val location = Res.string.location_detail__title.rememberString()
        val toolbarTitle = remember(location) {
            if (nav.parentNavigation == null) "$location (${nnvl.data.id}) of" to nnvl.data.company.name
            else location to "(${nnvl.data.id})"
        }
        Toolbar(
            nav = nav,
            titlePrefix = toolbarTitle.first,
            titleSuffix = toolbarTitle.second,
            actions = toolbarActions
        )

        if (display.isMobile || display.resolution.width < 1000) {
            Column(modifier = Modifier.fillMaxSize()) {
                EmergencyStatusRow(nnvl)
                PanelRight(
                    nnvl = nnvl,
                    tabs = vm.tabsScreenSmall,
                    selection = vm.selectedTabSmall.collectAsState(),
                    modifier = Modifier.fillMaxWidth().weight(1F)
                )
            }

        } else {
            Row(Modifier.fillMaxSize()) {
                Column(modifier = Modifier.width(384.dp).fillMaxHeight()) {
                    EmergencyStatusRow(nnvl)
                    PanelLeft(nnvl, Modifier.fillMaxWidth().weight(1F))
                }
                if (vm.tabsScreenLarge.isNotEmpty()) {
                    Spacer(
                        Modifier.width(4.dp)
                            .fillMaxHeight()
                            .background(MaterialTheme.colorScheme.tertiaryContainer)
                    )
                    PanelRight(
                        nnvl = nnvl,
                        tabs = vm.tabsScreenLarge,
                        selection = vm.selectedTabLarge.collectAsState(),
                        modifier = Modifier.weight(1F)
                    )
                }
            }
        }
    }
}

@Composable
fun LocationScope.GroupActionDialog(
    actionState: DialogState
) {

    Dialog(actionState) {
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            onDismissRequest = { actionState.hide() },
            title = { Text(Res.string.location_detail__group_action__title.rememberString()) },
            dismissButton = { TextButton({ actionState.hide() }) { Text(Res.string.dialog__action_cancel.rememberString()) } },
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(Res.string.location_detail__group_action__subtitle.rememberString())
                Space(8.dp)
                groupActions.forEach {
                    if (it.act == Message.Task.Keys.UNKNOWN) {
                        Space(8.dp)
                    } else {
                        Button(
                            modifier = Modifier.width(256.dp)
                                .align(Alignment.CenterHorizontally),
                            onClick = {
                                action(LocationVM.Action.GroupAction(it.act))
                                actionState.hide()
                            },
                        ) {
                            Text(it.name.rememberString())
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun LocationScope.DeleteDialog(deleteState: DialogState, countDevice: Int) {
    val confirmButton: @Composable () -> Unit = {
        if (countDevice == 0) {
            TextButton({
                action(LocationVM.Action.Delete)
                deleteState.hide()
            }) { Text(Res.string.dialog__action_delete.rememberString()) }
        }
    }
    Dialog(deleteState) {
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            onDismissRequest = { deleteState.hide() },
            title = { Text(Res.string.location_detail__delete_title.rememberString()) },
            confirmButton = confirmButton,
            dismissButton = { TextButton({ deleteState.hide() }) { Text(Res.string.dialog__action_cancel.rememberString()) } },
        ) {
            if (countDevice > 0) {
                Text(Res.string.location_detail__delete_warn_device.rememberString())
            } else {
                Text(Res.string.location_detail__delete_message.rememberString())
            }
        }
    }
}

@Composable
fun LocationScope.PanelLeft(nnvl: LocationScope.Wrapper, modifier: Modifier) {
    Box(
        modifier = modifier
    ) {
        LazyColumn(
            Modifier.fillMaxSize(),
            contentPadding = PaddingValues(8.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (nav.parentNavigation != null) {
                item {
                    Row(
                        Modifier.fillMaxWidth()
                            .padding(horizontal = 8.dp, vertical = 8.dp)
                    ) {
                        Icon(Svg.Store, null)
                        Space(16.dp)
                        Text(nnvl.data.company.name)
                    }
                }
            }
            item {
                TextField(
                    shape = RoundedCornerShape(topStart = 12.dp, topEnd = 12.dp),
                    value = nnvl.name.text,
                    readOnly = !nnvl.canEdit,
                    label = { Text(Res.string.location_detail__name_hint.rememberString()) },
                    leadingIcon = { Icon(Svg.Format_text, null) },
                    isError = nnvl.name.err,
                    modifier = Modifier
                        .fillMaxWidth(),
                    onValueChange = {
                        nnvl.name.set(it)
                    })
            }
            item {
                OpenningHours(nnvl)
            }
        }
    }
}

@Composable
fun LocationScope.PanelRight(
    modifier: Modifier,
    tabs: List<TabEntry<LocationVM.TabType>>,
    selection: State<TabEntry<LocationVM.TabType>?>,
    nnvl: LocationScope.Wrapper
) {
    Column(modifier) {
        Crossfade(selection.value, modifier = Modifier.weight(1F)) {
            when (it?.tag) {
                LocationVM.TabType.INFO -> {
                    PanelLeft(
                        nnvl = nnvl,
                        modifier = Modifier.fillMaxSize()
                    )
                }

                LocationVM.TabType.DEVICES -> {
                    Box(Modifier.fillMaxSize()) {
                        NavigationChildLocationContent(Node.DeviceListingUI, it.args)
                    }
                }

                LocationVM.TabType.BRANDING -> LocationBranding(
                    nnvl = nnvl,
                    modifier = Modifier.fillMaxSize()

                )

                LocationVM.TabType.BRANDING_PHOTOS -> LocationBrandingPhotos(
                    nnvl = nnvl,
                    modifier = Modifier.fillMaxSize()
                )

                LocationVM.TabType.EMERGENCY -> LocationEmergency(
                    nnvl = nnvl,
                    modifier = Modifier.fillMaxSize()

                )

                null -> Unit
            }
        }
        if (tabs.size > 1) {
            NavigationBar(Modifier.fillMaxWidth()) {
                tabs.forEach { tabDefinition ->
                    NavigationBarItem(
                        selected = tabDefinition.tag == selection.value?.tag,
                        onClick = {
                            with(tabDefinition) { onClick(tabDefinition) }
                        },
                        label = { Text(tabDefinition.label.rememberString()) },
                        icon = { Icon(tabDefinition.icon, null) })
                }
            }
        }
    }
}

@Composable
fun LocationScope.OpenningHours(nnvl: LocationScope.Wrapper) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(topStart = 12.dp, topEnd = 12.dp),
    ) {
        Column(Modifier.fillMaxWidth().padding(8.dp)) {
            Row(Modifier.padding(horizontal = 8.dp)) {
                Icon(Svg.Clock_outline, null)
                Space(16.dp)
                Text(
                    text = Res.string.location_detail__opening_hours.rememberString(),
                    modifier = Modifier.align(Alignment.CenterVertically)
                        .weight(1F),
                    style = MaterialTheme.typography.labelMedium
                )

            }
            Space(4.dp)
            nnvl.days.state.forEach {
                OpenningHoursRow(nnvl, it)
            }
        }
    }
}

@Composable
fun LocationScope.OpenningHoursRow(nnvl: LocationScope.Wrapper, item: LocationScope.WrapperDays.Item) {
    val stateStart = DialogState.remember()
    val stateEnd = DialogState.remember()
    TimePickerDialog(stateStart, item.start)
    TimePickerDialog(stateEnd, item.end)
    Row(
        Modifier.fillMaxWidth().padding(vertical = 4.dp)
            .padding(start = 4.dp)
    ) {
        Text(
            text = item.name.rememberString(),
            modifier = Modifier.width(40.dp).align(Alignment.CenterVertically)
        )
        Space(4.dp)
        IconicSwitch(
            checked = item.enabled.state,
            enabled = nnvl.canEdit,
            onCheckedChange = { item.enabled.set(it) },
            modifier = Modifier.align(Alignment.CenterVertically)
        )
        Space(8.dp)
        Button(
            onClick = { stateStart.show() },
            enabled = nnvl.canEdit && item.enabled.state,
            modifier = Modifier.weight(1F)
        ) {
            Text(item.start.toStringHM(), fontWeight = FontWeight.Black)
        }
        Space(8.dp)
        Button(
            onClick = { stateEnd.show() },
            enabled = nnvl.canEdit && item.enabled.state,
            modifier = Modifier.weight(1F)
        ) {
            Text(item.end.toStringHM(), fontWeight = FontWeight.Black)
        }
        AnimatedVisibility(
            modifier = Modifier.align(Alignment.CenterVertically),
            visible = nnvl.days.state.any {
                it.start.state != item.start.state
                    || it.end.state != item.end.state
                    || it.enabled.state != item.enabled.state
            }
        ) {
            Crossfade(
                targetState = nnvl.days.clipboard,
                modifier = Modifier.align(Alignment.CenterVertically)
                    .padding(start = 8.dp)
            ) {
                when {
                    it == null -> {
                        IconButton(
                            modifier = Modifier.align(Alignment.CenterVertically),
                            onClick = { nnvl.days.clipboard = item }) {
                            Icon(Svg.Content_copy, null)
                        }
                    }

                    it.data.day == item.data.day -> {
                        IconButton(
                            modifier = Modifier.align(Alignment.CenterVertically),
                            onClick = { nnvl.days.clipboard = null }) {
                            Icon(Svg.Check, null)
                        }
                    }

                    it.start.state == item.start.state
                        && it.end.state == item.end.state
                        && it.enabled.state == item.enabled.state -> {
                        Space(48.dp)
                    }

                    else -> {
                        IconButton(
                            modifier = Modifier.align(Alignment.CenterVertically),
                            onClick = {
                                item.start.set(it.start.state)
                                item.end.set(it.end.state)
                                item.enabled.set(it.enabled.state)
                            }) {
                            Icon(Svg.Content_paste, null)
                        }
                    }
                }
            }
        }

    }
}

@Composable
private fun EFieldRegularTime.toStringHM(): String {
    return remember(state) {
        buildString {
            append(state.hour.toString().padStart(2, '0'))
            append(":")
            append(state.minute.toString().padStart(2, '0'))
        }
    }
}

@Composable
private fun LocationScope.Wrapper.saveState(): State<Boolean> {
    val state = remember {
        mutableStateOf(false)
    }
    LaunchedEffect(change, branding.change) {
        state.value = change || branding.change
    }

    return state
}

@Composable
fun LocationScope.EmergencyStatusRow(nnvl: LocationScope.Wrapper) {
    AnimatedVisibility(
        visible = nnvl.emergency.active,
        modifier = Modifier.fillMaxWidth(),
        enter = fadeIn() + expandVertically(),
        exit = fadeOut() + shrinkVertically()
    ) {
        Surface(
            modifier = Modifier.fillMaxWidth(),
            contentColor = MaterialTheme.colorScheme.errorContainer,
            color = MaterialTheme.colorScheme.onErrorContainer
        ) {
            Row(
                modifier = Modifier.fillMaxWidth()
            ) {
                LazyImage(
                    id = nnvl.emergency.activeResource,
                    modifier = Modifier.size(128.dp),
                    alphaBackground = false,
                    contentScale = ContentScale.Crop
                )
                Column(
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.padding(16.dp)
                        .align(Alignment.CenterVertically)
                        .fillMaxWidth()
                ) {
                    Text(
                        text = Res.string.location_detail__emergency_status_message.rememberString(),
                        style = MaterialTheme.typography.titleLarge,
                        modifier = Modifier.align(Alignment.CenterHorizontally)
                    )
                    Button(
                        onClick = { action(LocationVM.Action.EmergencyDisable) },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(Res.string.location_detail__emergency_disable_action.rememberString())
                    }
                }
            }
        }
    }
}
